import type { DineRequisition, DineRequisitionPurpose, RequisitionStatus } from 'api/types';

/** Department requisitions (R1): the words the screens use for the server's codes. */

/**
 * Purposes are the tenant's list (useRequisitionPurposes, 2026-10-26). Inactive ones are marked; a code
 * a record still carries but the list no longer has is kept with the record's own name, so an edit or a
 * filter never shows a bare code or drops the value.
 */
export const purposeOptions = (purposes: DineRequisitionPurpose[], keep?: { code?: string | null; name?: string | null }) => {
  const options = purposes.map((p) => ({ value: p.purposeCode, label: p.isActive ? p.purposeName : `${p.purposeName} (inactive)` }));
  if (keep?.code && !options.some((o) => o.value === keep.code)) options.push({ value: keep.code, label: keep.name ?? keep.code });
  return options;
};

export const STATUS_LABEL: Record<RequisitionStatus, string> = {
  REQUESTED: 'Requested',
  PENDING_APPROVAL: 'Awaiting approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  SENT: 'With the kitchen',
  ISSUED: 'Issued',
  CANCELLED: 'Cancelled',
};

/** Badge class per status, from the template's badge set. */
export const STATUS_BADGE: Record<RequisitionStatus, string> = {
  REQUESTED: 'badge-inactive',
  PENDING_APPROVAL: 'badge-pending',
  APPROVED: 'badge-inactive',
  REJECTED: 'badge-error',
  SENT: 'badge-pending',
  ISSUED: 'badge-active',
  CANCELLED: 'badge-error',
};

/** Not yet with the kitchen: can still change or be cancelled (S3). */
export const beforeKitchen = (s: RequisitionStatus) => s === 'REQUESTED' || s === 'PENDING_APPROVAL' || s === 'APPROVED';

/** Units a requisition may be raised for, as the server checks them. */
export const REQUESTING_UNIT_TYPES = ['DEPARTMENT', 'COST_CENTER'];

export const money = (n?: number | null) => (n ?? 0).toFixed(2);

export const dateOnly = (iso?: string | null) => (iso ? iso.slice(0, 10) : '');

export const dateTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '');

/** Where the kitchen is with a sent requisition, e.g. "2 of 3 ready". */
export const kitchenProgress = (r: { requisitionStatus: RequisitionStatus; ticketsReady: number; ticketsLive: number }) => {
  if (r.requisitionStatus !== 'SENT') return '';
  if (r.ticketsLive === 0) return 'Not reached the kitchen';
  return r.ticketsReady === r.ticketsLive ? 'Ready to issue' : `${r.ticketsReady} of ${r.ticketsLive} ready`;
};

/** Dishes sent but held back by a closed station: they go when the requisition is sent again. */
export const heldBack = (r: DineRequisition) =>
  r.lines ? r.lines.filter((l) => l.orderLineId && l.lineStatus === 'PENDING').length : (r.heldBack ?? 0);

export interface RequisitionRights { canEdit: boolean; canIssue: boolean; canCancel: boolean }

/**
 * What can be done with a requisition now. One rule for the view's footer and the list's row menu
 * (design 5.1), so an action never shows in one place and not the other. `notHere` / `notReady` say
 * why an action that applies is disabled; the server checks the same again.
 */
export const requisitionActions = (r: DineRequisition, workingOutletId: string, rights: RequisitionRights) => {
  const held = heldBack(r);
  const notHere = workingOutletId === r.dineOutletId ? undefined : `Switch the working outlet (top bar) to ${r.outletName} to send or issue it.`;
  const ready = r.ticketsLive > 0 && r.ticketsReady === r.ticketsLive && held === 0;
  return {
    held,
    notHere,
    ready,
    notReady: ready ? undefined : 'The kitchen has not marked every ticket ready yet.',
    mayEdit: rights.canEdit && beforeKitchen(r.requisitionStatus) && !r.orderId,
    maySend: rights.canIssue && (r.requisitionStatus === 'REQUESTED' || r.requisitionStatus === 'APPROVED' || (r.requisitionStatus === 'SENT' && held > 0)),
    mayIssue: rights.canIssue && r.requisitionStatus === 'SENT',
    mayCancel: rights.canCancel && beforeKitchen(r.requisitionStatus),
    /** Approve / Reject (S3): the server says whether the caller holds the role the request is routed to. */
    mayDecide: r.requisitionStatus === 'PENDING_APPROVAL' && !!r.canDecide,
    awaiting: r.requisitionStatus === 'PENDING_APPROVAL' ? `Awaiting approval by ${r.approverRole ?? 'the approver'}.` : undefined,
  };
};
